import {
  DISCOVERY_LOCATION_FILTERS,
  DISCOVERY_LOCATION_LABELS,
  type DiscoveryLocationFilter,
} from '@hearth/shared';
import type {
  DiscoveryBulkResponse,
  DiscoveryImportResponse,
  DiscoveryMatchFilter,
  DiscoveryMergeResponse,
  DiscoveryResultDTO,
  DiscoveryReviewStatus,
  DiscoverySearchDetailDTO,
  DiscoveryWebsiteEmailResponse,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, GitMerge, RefreshCw, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ScorePill } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, Checkbox, PageSpinner, Spinner, Tabs, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { pluralize } from '../../lib/format';
import { SEARCH_STATUS_COLORS } from './DiscoveryPage';
import { lookupSummary } from './model';
import { WebsiteEmail } from './WebsiteEmail';

type StatusTab = 'pending' | 'imported' | 'merged' | 'rejected';
type MatchTab = 'all' | DiscoveryMatchFilter;

const MATCH_LABELS: Record<DiscoveryResultDTO['matchKind'], { label: string; color: string }> = {
  new: { label: 'New', color: 'green' },
  in_crm: { label: 'Already in CRM', color: 'slate' },
  possible_duplicate: { label: 'Possible duplicate', color: 'amber' },
};

const REVIEW_COLORS: Record<DiscoveryReviewStatus, string> = { pending: 'sky', imported: 'green', merged: 'violet', rejected: 'gray' };

export function DiscoverySearchPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [status, setStatus] = useState<StatusTab>('pending');
  const [match, setMatch] = useState<MatchTab>('all');
  const [hideRejected, setHideRejected] = useState(true);
  const [hasEmail, setHasEmail] = useState(false);
  /** Until the search loads (or the user clicks a chip) there is no size filter. */
  const [locationFilter, setLocationFilter] = useState<DiscoveryLocationFilter>('any');
  const [sizeTouched, setSizeTouched] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const params = new URLSearchParams({ status, pageSize: '200', hidePreviouslyRejected: String(hideRejected) });
  if (match !== 'all') params.set('match', match);
  if (hasEmail) params.set('hasEmail', 'true');
  // Once the user picks a size, send it explicitly — including "any", which must not fall back to the
  // size the search was created with. Until they touch it, the server applies the search's own size.
  if (sizeTouched) params.set('locations', locationFilter);
  const key = ['discovery', 'searches', id, params.toString()];
  const { data } = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.get<DiscoverySearchDetailDTO>(`/api/discovery/searches/${id}?${params}`, { signal }),
    // Poll fast while the search runs, then slower while websites are still being read for addresses.
    refetchInterval: (query) => {
      const detail = query.state.data;
      if (!detail) return false;
      if (detail.search.status === 'queued' || detail.search.status === 'running') return 2_000;
      return detail.summary.websitesPending > 0 ? 4_000 : false;
    },
  });

  // Adopt the size the search was created with, so a "chains" search does not need the user to re-filter.
  useEffect(() => {
    if (!sizeTouched && data) setLocationFilter(data.search.locations);
  }, [data, sizeTouched]);

  const refresh = async () => {
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: ['discovery'] });
    await queryClient.invalidateQueries({ queryKey: ['leads'] });
  };
  const importOne = useMutation({
    mutationFn: (resultId: string) => api.post<DiscoveryImportResponse>(`/api/discovery/results/${resultId}/import`, {}),
    onSuccess: async (result) => {
      toast.success(result.warnings.length > 0 ? `Imported — ${result.warnings.join(' ')}` : 'Imported as a new lead');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const mergeOne = useMutation({
    mutationFn: ({ resultId, businessId }: { resultId: string; businessId: string }) =>
      api.post<DiscoveryMergeResponse>(`/api/discovery/results/${resultId}/merge`, { businessId }),
    onSuccess: async (result) => {
      toast.success(result.filled.length > 0 ? `Merged — filled ${result.filled.join(', ')}` : 'Merged — nothing new to add');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const rejectOne = useMutation({
    mutationFn: (resultId: string) => api.post(`/api/discovery/results/${resultId}/reject`, {}),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const bulk = useMutation({
    mutationFn: (action: 'import' | 'reject') => api.post<DiscoveryBulkResponse>('/api/discovery/results/bulk', { ids: [...selected], action }),
    onSuccess: async (result, action) => {
      toast.success(`${pluralize(result.succeeded, 'result')} ${action === 'import' ? 'imported' : 'rejected'}${result.failed ? `, ${result.failed} failed` : ''}${result.skipped ? `, ${result.skipped} skipped` : ''}`);
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const lookAgain = useMutation({
    mutationFn: (result: DiscoveryResultDTO) =>
      api.post<DiscoveryWebsiteEmailResponse>(`/api/discovery/results/${result.id}/website-email`, {}),
    onSuccess: async ({ lookup }, result) => {
      if (lookup.status === 'found') toast.success(lookupSummary(lookup));
      else toast.info(lookupSummary(lookup));
      // An imported or merged result applies the address to its lead as well.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['discovery', 'searches', id] }),
        result.businessId ? queryClient.invalidateQueries({ queryKey: ['leads', result.businessId] }) : null,
      ]);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!data) return <PageSpinner />;
  const { search, results, summary } = data;
  const running = search.status === 'queued' || search.status === 'running';
  const toggle = (resultId: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(resultId);
    else next.delete(resultId);
    setSelected(next);
  };
  const pending = results.items.filter((r) => r.reviewStatus === 'pending');
  const attribution = [...new Set(results.items.map((r) => r.attribution).filter(Boolean))];

  return (
    <Page width="wide">
      <PageHeader
        title={`${search.query} — ${search.locationText ?? 'map area'}`}
        breadcrumbs={[{ label: 'Discover', to: '/discover' }, { label: search.query }]}
        meta={<Badge color={SEARCH_STATUS_COLORS[search.status]}>{running ? 'Searching…' : search.status}</Badge>}
        description={`${search.providerLabel} · ${search.radiusKm ?? '?'} km · ${search.resultCount} found, ${search.newCount} not in the CRM`}
      />
      {search.error && <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">{search.error}</p>}
      {search.warning && <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">{search.warning}</p>}

      <Card>
        <Tabs
          tabs={[
            { key: 'pending', label: 'To review', count: summary.pending },
            { key: 'imported', label: 'Imported', count: summary.imported },
            { key: 'merged', label: 'Merged', count: summary.merged },
            { key: 'rejected', label: 'Rejected', count: summary.rejected },
          ]}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setSelected(new Set());
          }}
        />
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-[13px]">
          {(
            [
              ['all', 'All'],
              ['new', `New (${summary.new})`],
              ['possible_duplicate', `Possible duplicates (${summary.possibleDuplicate})`],
              ['in_crm', `Already in CRM (${summary.inCrm})`],
            ] as const
          ).map(([value, label]) => (
            <Button key={value} size="sm" variant={match === value ? 'secondary' : 'ghost'} onClick={() => setMatch(value)}>
              {label}
            </Button>
          ))}
          <Button size="sm" variant={hasEmail ? 'secondary' : 'ghost'} aria-pressed={hasEmail} onClick={() => setHasEmail(!hasEmail)}>
            Has email ({summary.withEmail})
          </Button>
          {summary.websitesPending > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <Spinner size={12} /> Reading {pluralize(summary.websitesPending, 'website')} for email addresses…
            </span>
          )}
          <label className="ml-auto flex items-center gap-2 text-slate-600">
            <Checkbox checked={hideRejected} onChange={(event) => setHideRejected(event.target.checked)} />
            Hide places rejected before
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-[13px]">
          <span className="text-slate-500">Locations:</span>
          {DISCOVERY_LOCATION_FILTERS.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={locationFilter === option ? 'secondary' : 'ghost'}
              onClick={() => {
                setLocationFilter(option);
                setSizeTouched(true);
              }}
            >
              {DISCOVERY_LOCATION_LABELS[option]}
            </Button>
          ))}
          {!sizeTouched && data && (
            <span className="text-xs text-slate-400">this search asked for: {DISCOVERY_LOCATION_LABELS[data.search.locations]}</span>
          )}
        </div>
        {status === 'pending' && selected.size > 0 && (
          <div className="flex items-center gap-2 border-b border-slate-100 bg-ember-50 px-4 py-2 text-[13px]">
            <span>{selected.size} selected</span>
            <Button size="sm" variant="primary" icon={Check} loading={bulk.isPending} onClick={() => bulk.mutate('import')}>
              Import as leads
            </Button>
            <Button size="sm" icon={X} loading={bulk.isPending} onClick={() => bulk.mutate('reject')}>
              Reject
            </Button>
          </div>
        )}
        {status === 'pending' && (
          <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
            Addresses found on a business’s own website are imported with implied consent (a published address) and the page as evidence, unless the site
            asks for no solicitations.
          </p>
        )}

        <ul className="divide-y divide-slate-100">
          {status === 'pending' && pending.length > 0 && (
            <li className="flex items-center gap-2 px-4 py-2 text-xs text-slate-500">
              <Checkbox
                aria-label="Select all"
                checked={pending.every((r) => selected.has(r.id))}
                onChange={(event) => setSelected(event.target.checked ? new Set(pending.map((r) => r.id)) : new Set())}
              />
              Select all on this page
            </li>
          )}
          {results.items.map((result) => {
            const matchInfo = MATCH_LABELS[result.matchKind];
            return (
              <li key={result.id} className="flex flex-wrap items-start gap-3 px-4 py-3 text-[13px]">
                {result.reviewStatus === 'pending' && (
                  <Checkbox aria-label={`Select ${result.name}`} checked={selected.has(result.id)} onChange={(event) => toggle(result.id, event.target.checked)} className="mt-1" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{result.name}</span>
                    <Badge color={matchInfo.color} size="sm">
                      {matchInfo.label}
                      {result.matchKind === 'possible_duplicate' && result.matchConfidence !== null ? ` ${result.matchConfidence}%` : ''}
                    </Badge>
                    {result.matchedBusiness && (
                      <Link to={`/leads/${result.matchedBusiness.id}`} className="text-ember-700 hover:underline">
                        {result.matchedBusiness.name}
                      </Link>
                    )}
                    {result.brand && (
                      <Badge color="sky" size="sm">
                        {result.brand} · {result.branchCount} {result.branchCount === 1 ? 'branch' : 'branches'} here
                      </Badge>
                    )}
                    {!result.brand && <Badge size="sm">One shop</Badge>}
                    {result.previouslyRejected > 0 && <Badge size="sm">Rejected {result.previouslyRejected}× before</Badge>}
                    {result.operatingStatus !== 'operating' && <Badge color="red" size="sm">{result.operatingStatus.replace(/_/g, ' ')}</Badge>}
                    {result.reviewStatus !== 'pending' && (
                      <Badge color={REVIEW_COLORS[result.reviewStatus]} size="sm">
                        {result.reviewStatus}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-600">
                    {[result.addressLine1, result.city, result.region].filter(Boolean).join(', ') || 'No address'}
                    {result.phone && ` · ${result.phone}`}
                    {result.website && (
                      <>
                        {' · '}
                        <a href={result.website} target="_blank" rel="noreferrer" className="text-ember-700 hover:underline">
                          {result.website.replace(/^https?:\/\//, '')}
                        </a>
                      </>
                    )}
                  </p>
                  <p className="text-slate-600">
                    <WebsiteEmail result={result} />
                    {result.website && !result.email && can('discovery:run') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={RefreshCw}
                        className="ml-1"
                        loading={lookAgain.isPending && lookAgain.variables?.id === result.id}
                        onClick={() => lookAgain.mutate(result)}
                      >
                        Look again
                      </Button>
                    )}
                    {result.operator && <span className="text-slate-500"> · run by {result.operator}</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {[result.cuisine, result.category].filter(Boolean).join(' · ')}
                    {result.rating !== null && (
                      <span className="ml-2 inline-flex items-center gap-0.5">
                        <Star className="size-3" /> {result.rating} ({result.ratingCount ?? 0})
                      </span>
                    )}
                    {result.estimatedScore.reasons.length > 0 && ` · ${result.estimatedScore.reasons.join(', ')}`}
                  </p>
                </div>
                <ScorePill score={result.estimatedScore.score} tierKey={result.estimatedScore.tierKey} />
                {result.reviewStatus === 'pending' ? (
                  <div className="flex gap-1">
                    {result.matchedBusiness ? (
                      <Button size="sm" icon={GitMerge} loading={mergeOne.isPending} onClick={() => mergeOne.mutate({ resultId: result.id, businessId: result.matchedBusiness!.id })}>
                        Merge
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant={result.matchKind === 'new' ? 'primary' : 'secondary'}
                      loading={importOne.isPending}
                      onClick={() =>
                        void (async () => {
                          if (
                            result.matchKind !== 'new' &&
                            !(await confirm({
                              title: 'Import anyway?',
                              message: `This looks like ${result.matchedBusiness?.name ?? 'a lead already in the CRM'}. Importing creates a separate lead; merging keeps one.`,
                              confirmLabel: 'Import as new lead',
                            }))
                          )
                            return;
                          importOne.mutate(result.id);
                        })()
                      }
                    >
                      Import
                    </Button>
                    <Button size="sm" variant="ghost" icon={X} aria-label={`Reject ${result.name}`} onClick={() => rejectOne.mutate(result.id)}>
                      Reject
                    </Button>
                  </div>
                ) : result.businessId ? (
                  <Link to={`/leads/${result.businessId}`} className="text-ember-700 hover:underline">
                    Open lead
                  </Link>
                ) : null}
              </li>
            );
          })}
          {results.items.length === 0 && (
            <li className="px-4 py-8 text-center text-[13px] text-slate-500">{running ? 'Searching…' : 'Nothing here with these filters.'}</li>
          )}
        </ul>
        {attribution.length > 0 && <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">Data: {attribution.join(' · ')}</p>}
      </Card>
    </Page>
  );
}
